import { useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import { Dialog, DialogContent, DialogHeader, DialogTitle, DialogDescription } from "@/components/ui/dialog";
import { Sparkles, Coins, Crown, Loader2, Check } from "lucide-react";
import api from "@/lib/api";
import { toast } from "sonner";
import { useCredits } from "@/context/CreditsContext";
import useIsMobile from "@/lib/useIsMobile";

// Global paywall. When any premium action returns 402, lib/paywall#showPaywall fires an
// "rp-paywall" event with the backend detail; this pops a modal with a one-tap Upgrade and
// (when enabled) inline credit-pack buy buttons — no dead-end error.
export default function PaywallModal() {
  const nav = useNavigate();
  const m = useIsMobile();
  const { wallet, credits } = useCredits();
  const [open, setOpen] = useState(false);
  const [detail, setDetail] = useState({});
  const [cfg, setCfg] = useState(null);
  const [buying, setBuying] = useState(null);

  useEffect(() => {
    const fn = (e) => { setDetail(e.detail || {}); setOpen(true); };
    window.addEventListener("rp-paywall", fn);
    return () => window.removeEventListener("rp-paywall", fn);
  }, []);

  // Prefer the live wallet; fall back to a light config fetch for packs / purchase flag.
  useEffect(() => {
    if (!open) return;
    if (wallet?.packs) { setCfg({ packs: wallet.packs, purchase_enabled: wallet.purchase_enabled }); return; }
    api.get("/credits/config").then((r) => setCfg(r.data)).catch(() => setCfg({ packs: [], purchase_enabled: false }));
  }, [open, wallet]);

  const bal = typeof credits === "number" ? credits : (typeof detail.have === "number" ? detail.have : null);
  const packs = cfg?.packs || [];
  const purchaseEnabled = cfg?.purchase_enabled;

  const goUpgrade = () => { setOpen(false); nav("/choose-plan"); };
  const buy = async (pack) => {
    if (!purchaseEnabled) { toast.info("Buying credits is coming soon — payments aren't live yet."); return; }
    setBuying(pack.id);
    try {
      const { data } = await api.post("/credits/checkout", { pack_id: pack.id, origin_url: window.location.origin });
      window.location.href = data.checkout_url;
    } catch (e) {
      toast.error("Couldn't start checkout — try again");
      setBuying(null);
    }
  };

  const dark = m;
  const card = dark ? "bg-white/5 border-white/10" : "bg-white border-[#E6E8EC]";
  const sub = dark ? "text-slate-400" : "text-slate-500";
  const title = dark ? "text-white" : "text-slate-900";

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogContent className={`${dark ? "bg-[#0C1017] text-slate-100 border-white/10" : "bg-[#F7F8FA] text-slate-900 border-[#E6E8EC]"} max-w-md`} data-testid="paywall-modal">
        <DialogHeader>
          <div className="flex items-center gap-2">
            <span className="w-9 h-9 rounded-full bg-[#C8000F]/15 flex items-center justify-center shrink-0"><Sparkles className="w-5 h-5 text-[#C8000F]" /></span>
            <div>
              <DialogTitle className={`font-display ${title}`}>Keep going with Rodeo Pilot</DialogTitle>
              {bal != null && <DialogDescription className={sub}>You have {bal} credit{bal === 1 ? "" : "s"} left</DialogDescription>}
            </div>
          </div>
        </DialogHeader>

        <p className={`text-sm ${dark ? "text-slate-300" : "text-slate-600"}`} data-testid="paywall-message">
          {detail.message || "You've used your free trial for this feature. Upgrade your plan for unlimited, or top up with credits."}
        </p>

        {/* Upgrade */}
        <button onClick={goUpgrade} data-testid="paywall-upgrade" className="w-full flex items-center justify-center gap-2 bg-[#C8000F] text-white text-sm font-semibold py-3 rounded-xl active:opacity-90 hover:opacity-95 transition-opacity">
          <Crown className="w-4 h-4" /> Upgrade for unlimited
        </button>

        {/* Buy credits */}
        <div className="pt-1">
          <div className={`flex items-center gap-2 text-[11px] uppercase font-bold ${sub} mb-2`}><Coins className="w-3.5 h-3.5" /> Or top up credits</div>
          <div className="space-y-2 max-h-[46vh] overflow-y-auto -mx-1 px-1">
            {packs.map((p) => {
              const per = p.amount / p.credits;
              const save = Math.round((1 - per) * 100);
              return (
              <button key={p.id} onClick={() => buy(p)} disabled={!!buying} data-testid={`paywall-pack-${p.id}`}
                className={`w-full flex items-center justify-between gap-3 rounded-xl border px-4 py-2.5 ${card} ${dark ? "active:bg-white/10" : "hover:border-[#C8000F]/50"} transition-colors disabled:opacity-60`}>
                <span className="flex flex-col items-start min-w-0">
                  <span className="flex items-center gap-2 flex-wrap">
                    <span className={`text-[15px] font-bold ${title}`}>{p.credits} credits</span>
                    {p.popular && <span className="text-[10px] font-bold uppercase bg-[#C8000F] text-white px-1.5 py-0.5 rounded">Popular</span>}
                    {p.best_value && <span className="text-[10px] font-bold uppercase px-1.5 py-0.5 rounded" style={{ background: "#E6B35A", color: "#111" }}>Best Value</span>}
                  </span>
                  <span className={`text-[11px] ${sub}`}>${per.toFixed(2)} per credit{save > 0 ? ` · Save ${save}%` : ""}</span>
                </span>
                <span className="flex items-center gap-2 shrink-0">
                  <span className={`text-sm font-semibold ${title}`}>${p.amount} {(p.currency || "cad").toUpperCase()}</span>
                  {buying === p.id ? <Loader2 className="w-4 h-4 animate-spin text-[#C8000F]" /> : <span className="text-[13px] font-semibold text-[#C8000F]">{purchaseEnabled ? "Buy" : "Soon"}</span>}
                </span>
              </button>
              );
            })}
            {!packs.length && <div className={`text-xs ${sub} py-1`}>Loading options…</div>}
          </div>
          <p className={`text-[11px] ${sub} mt-2 flex items-center gap-1`}><Check className="w-3 h-3" /> Credits never expire · secure checkout</p>
        </div>
      </DialogContent>
    </Dialog>
  );
}
